import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';

const GuaranteeSection = () => {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    whatsAppNo: '',
    email: '',
    businessDetails: ''
  });

  const handleClose = () => {
    setShowDemoModal(false);
    setShowSuccess(false);
    setIsSubmitting(false);
    setErrorMessage('');
    setFormData({
      firstName: '',
      lastName: '',
      whatsAppNo: '',
      email: '',
      businessDetails: ''
    });
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Please enter your first name');
      return false;
    }
    if (!formData.whatsAppNo.trim()) {
      setErrorMessage('Please enter your WhatsApp number');
      return false;
    }
    if (formData.whatsAppNo.length < 10) {
      setErrorMessage('Please enter a valid WhatsApp number (minimum 10 digits)');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Here we would make our actual API call
      // Example:
      // const response = await axios.post('/api/demo-request', {
      //   ...formData,
      //   requestType: 'demo',
      //   timestamp: new Date().toISOString()
      // });

      console.log('Demo request submitted:', {
        ...formData,
        requestType: 'demo',
        timestamp: new Date().toISOString()
      });

      setShowSuccess(true);
      
      // Auto-close modal after 3 seconds - used temporarily, dismiss the modal after success response
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      console.error('Demo request failed:', error);
      setErrorMessage('Sorry, there was an error submitting your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="py-5 px-4 bg-secondary-subtle rounded">
        <div className="container">
          <div className="row align-items-center g-4">
            {/* Left Content */}
            <div className="col-12 col-md-7">
              <h2 className="mb-3">Try our product</h2>
              <p className="mb-4">
                Every Nurturix subscription includes a 20-day money-back guarantee. Cancel anytime, no
                questions asked.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <button className="btn btn-dark"  onClick={() => window.location.href = "/products"}> Start Free Trial</button>
                <button 
                  className="btn btn-outline-dark"
                  onClick={() => setShowDemoModal(true)}
                >
                  Ask for Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="col-12 col-md-5 text-center text-md-end">
              <img
                src="/assets/images/moneyback.png"
                alt="Guarantee"
                className="img-fluid rounded shadow-sm"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Demo Request Modal */}
      <Modal show={showDemoModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>Request Demo</span>
            <img 
              src="/assets/images/logo.png" 
              alt="Nurturix Logo" 
              style={{ width: '30px', height: 'auto' }}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showSuccess ? (
            <div className="text-center py-4">
              <Alert variant="success" className="border-0 bg-success bg-opacity-10">
                <h4 className="text-success mb-3">🎉 Demo Request Submitted!</h4>
                <p className="mb-2">Thank you for your interest in Nurturix!</p>
                <p className="mb-0 text-muted">
                  Our team will contact you within 24 hours to schedule your personalized demo.
                </p>
              </Alert>
            </div>
          ) : (
            <>
              {errorMessage && (
                <Alert variant="danger" className="mb-3" onClose={() => setErrorMessage('')} dismissible>
                  {errorMessage}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your first name"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your last name"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>WhatsApp Number *</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">+91</span>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your WhatsApp number"
                          value={formData.whatsAppNo}
                          onChange={(e) => setFormData({ ...formData, whatsAppNo: e.target.value })}
                          disabled={isSubmitting}
                        />
                      </div>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-4">
                  <Form.Label>Business Details / How Nurturix Can Help</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="A little detail about yourself and/or your company and which product of ours can help you to grow and give a piece of mind."
                    value={formData.businessDetails}
                    onChange={(e) => setFormData({ ...formData, businessDetails: e.target.value })}
                    disabled={isSubmitting}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button 
                    variant="secondary" 
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="text-white fw-semibold"
                    style={{ backgroundColor: '#CC5500', border: 'none' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Demo Request'
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default GuaranteeSection;
